// src/components/treasure/TreasureTrail.js
//
// THE TREASURE MAP (Nov 2026). A painted map with a dotted trail; every
// topic is a treasure chest sitting BESIDE the trail. Your avatar stands
// where you last were. Tap a chest and the avatar WALKS along the trail to
// it — past the other chests — and across pages when the trail runs off the
// edge (out of the forest on one page, into the desert on the next). Then
// "Play" starts that topic's game. Finish the game and its chest opens and
// stays open.
//
// Opening the map from a topic's Game button (targetSlug) walks you from
// your last spot straight to that topic.
//
// Props:
//   topics       the topics in order (already filtered for what's visible)
//   lockedFrom   index of the first topic a free account can't open
//   progress     { slug: { game: bool } }
//   targetSlug   walk here on arrival
//   onPlay(slug) starts that topic's game
//   spotKey      where to remember the avatar's spot (per user + class)
//   onLocked(topic), title
//   onArrive(slug) the avatar arrived at a chest (the page saves the spot so
//                  the teacher can see it)
//   classmates     TEACHER VIEW: [{ uid, name, spot (slug), done: { slug: true } }]
//                  → each student's avatar stands at their spot, every chest
//                  shows how many opened it, and a chest's card lists who
//                  opened it and who hasn't yet
//   onKidClick(uid)  teacher view: a student's avatar / name was tapped
//   goal           the class goal { target, count, by, reached } (or null)
//   onGoalClick    teacher view: "🎯 Class goal" button

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AvatarWithOutfit from "../avatar/AvatarWithOutfit";
import ChestArt from "../rewards/ChestArt";
import { useAvatarLooks, useMyAvatar } from "../../services/avatarService";
import { useMyCloset } from "../../services/shopService";
import { launchConfetti } from "../../utils/confetti";
import { START_F, chestPoint, layoutChests, mapName, measure, pageFor, pointAt, regionAt, useMapPages } from "./mapPages";
import "../rewards/rewards.css"; // the chest lid swings open (.sbl-chest-art.is-open)
import "./treasureTrail.css";

const SPEED = 34; // ms per trail unit (a whole page ≈ 4–5 s)
const read = (k) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const niceDate = (ymd) => {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y) return "";
  const dt = new Date(y, m - 1, d);
  return `${DAYS[dt.getDay()]}, ${MONTHS[m - 1]} ${d}`;
};
const todayNY = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

/** 🎯 the class goal: "Open 50 chests by Fri, Nov 7" with a progress bar */
const GoalBar = ({ goal, teacher, onEdit }) => {
  if (!goal) {
    return (
      <div className="tt-goal is-empty">
        <span>🎯 Set a class goal — when the class opens enough chests, everyone gets a golden gift!</span>
        <button type="button" className="tt-btn" onClick={onEdit}>
          🎯 Set a class goal
        </button>
      </div>
    );
  }
  const target = Number(goal.target) || 1;
  const count = Math.min(target, Number(goal.count) || 0);
  const pct = Math.round((count / target) * 100);
  const late = goal.by && goal.by < todayNY() && !goal.reached;
  return (
    <div className={`tt-goal ${goal.reached ? "is-reached" : ""} ${late ? "is-late" : ""}`}>
      <div className="tt-goal-text">
        <b>🎯 Class goal:</b> open <b>{target}</b> chests{goal.by ? ` by ${niceDate(goal.by)}` : ""}
        {goal.reached ? " — 🎉 REACHED! Everyone got a golden gift 🎁" : late ? " — time's up! Set a new goal." : ""}
      </div>
      <div className="tt-goal-track" role="progressbar" aria-valuemin={0} aria-valuemax={target} aria-valuenow={count}>
        <span className="tt-goal-fill" style={{ width: `${pct}%` }} />
        <span className="tt-goal-n">
          {count} / {target}
        </span>
      </div>
      {teacher && onEdit && (
        <button type="button" className="tt-btn is-soft" onClick={onEdit}>
          {goal.reached || late ? "New goal" : "Change"}
        </button>
      )}
    </div>
  );
};

const TreasureTrail = ({ topics = [], lockedFrom = Infinity, progress = {}, targetSlug = null, onPlay, spotKey = "sbl-map-spot", onLocked, title = "", onArrive, classmates = null, onKidClick, goal = null, onGoalClick }) => {
  const pages = useMapPages();
  const me = useMyAvatar();
  const closet = useMyCloset();
  const { chests, pageCount } = useMemo(() => layoutChests(topics, pages), [topics, pages]);
  const bySlug = useMemo(() => Object.fromEntries(chests.map((c) => [c.topic.slug, c])), [chests]);

  // where the avatar is: { page, f }
  const startAt = () => {
    const c = bySlug[read(spotKey)];
    return c ? { page: c.page, f: c.f } : { page: 0, f: START_F };
  };
  const [pos, setPos] = useState(startAt);
  const [walking, setWalking] = useState(false);
  const [facing, setFacing] = useState(1);
  const [card, setCard] = useState(null); // the chest we arrived at
  const [turning, setTurning] = useState(false); // page-turn fade
  const [width, setWidth] = useState(1000);
  const boxRef = useRef(null);
  const anim = useRef(0);
  const arrivedOnce = useRef(false);

  // re-place the avatar once the chests are known (the saved spot is a topic)
  useEffect(() => {
    if (!walking && !arrivedOnce.current) setPos(startAt());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chests.length]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => () => cancelAnimationFrame(anim.current), []);

  const page = pageFor(pages, pos.page);
  const m = useMemo(() => measure(page.trail, page.aspect || 16 / 9), [page]);

  // ---- walking ----
  const animate = (pageNo, from, to, fast) =>
    new Promise((resolve) => {
      const pg = pageFor(pages, pageNo);
      const mm = measure(pg.trail, pg.aspect || 16 / 9);
      const ms = Math.max(500, Math.min(fast ? 1800 : 5000, Math.abs(to - from) * mm.total * SPEED * (fast ? 0.45 : 1)));
      const t0 = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - t0) / ms);
        const e = k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2; // ease in-out
        const f = from + (to - from) * e;
        const p = pointAt(pg.trail, mm, f);
        setFacing((to >= from ? 1 : -1) * (p.dx >= 0 ? 1 : -1) || 1);
        setPos({ page: pageNo, f });
        if (k < 1) anim.current = requestAnimationFrame(step);
        else resolve();
      };
      anim.current = requestAnimationFrame(step);
    });
  const turnPage = async (pageNo, f) => {
    setTurning(true);
    await new Promise((r) => setTimeout(r, 380));
    setPos({ page: pageNo, f });
    await new Promise((r) => setTimeout(r, 60));
    setTurning(false);
  };

  const walkTo = useCallback(
    async (chest) => {
      if (!chest || walking) return;
      setCard(null);
      setWalking(true);
      let cur = { ...pos };
      if (chest.page === cur.page) {
        await animate(cur.page, cur.f, chest.f);
      } else {
        // a different map: turn straight to it (no walking through the maps
        // in between), then a short walk in from the edge to the chest
        const dir = chest.page > cur.page ? 1 : -1;
        const edge = dir > 0 ? 0 : 1;
        const startF = Math.abs(chest.f - edge) > 0.3 ? chest.f - dir * 0.3 : edge;
        await turnPage(chest.page, startF);
        await animate(chest.page, startF, chest.f, true);
      }
      cur = { page: chest.page, f: chest.f };
      setPos(cur);
      setWalking(false);
      arrivedOnce.current = true;
      write(spotKey, chest.topic.slug);
      onArrive?.(chest.topic.slug);
      setCard(chest);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [walking, pos, pages, spotKey, onArrive]
  );

  // came from a topic's Game button: walk there
  const walkedToTarget = useRef(false);
  const targetTimer = useRef(0);
  const walkToRef = useRef(walkTo);
  walkToRef.current = walkTo;
  useEffect(() => () => clearTimeout(targetTimer.current), []);
  useEffect(() => {
    if (walkedToTarget.current || !targetSlug || !bySlug[targetSlug]) return undefined;
    walkedToTarget.current = true;
    const c = bySlug[targetSlug];
    const here = bySlug[read(spotKey)];
    targetTimer.current = setTimeout(() => {
      if (here && here.topic.slug === targetSlug) setCard(c);
      else walkToRef.current(c);
    }, 700);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSlug, bySlug]);

  // chests that opened since this device last looked: play the opening
  const seenKey = `${spotKey}:open`;
  const [opening, setOpening] = useState(() => new Set());
  useEffect(() => {
    let seen = [];
    try {
      seen = JSON.parse(read(seenKey) || "[]");
    } catch {
      seen = [];
    }
    const fresh = chests.filter((c) => progress[c.topic.slug]?.game && !seen.includes(c.topic.slug)).map((c) => c.topic.slug);
    if (!fresh.length) return;
    setOpening(new Set(fresh));
    launchConfetti();
    write(seenKey, JSON.stringify([...seen, ...fresh]));
    const t = setTimeout(() => setOpening(new Set()), 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, chests.length]);

  // ---- teacher view: the class on the map ----
  const teacher = Array.isArray(classmates);
  const [showClass, setShowClass] = useState(true);
  const kidLooks = useAvatarLooks(teacher ? classmates.map((k) => k.uid) : []);
  // where each student stands: the chest they last walked to, else their
  // next unopened chest (else the last one)
  const kidsAt = useMemo(() => {
    const at = {};
    if (!teacher) return at;
    classmates.forEach((k) => {
      let c = k.spot && bySlug[k.spot];
      if (!c) c = chests.find((x) => !k.done?.[x.topic.slug]) || chests[chests.length - 1];
      if (!c) return;
      (at[c.topic.slug] = at[c.topic.slug] || []).push(k);
    });
    return at;
  }, [teacher, classmates, bySlug, chests]);
  const openedBy = (slug) => (teacher ? classmates.filter((k) => k.done?.[slug]) : []);
  const classOpened = teacher ? chests.reduce((n, c) => n + openedBy(c.topic.slug).length, 0) : 0;

  // ---- drawing ----
  const here = pointAt(page.trail, m, pos.f);
  const aspect = page.aspect || 16 / 9;
  const avatarH = Math.max(70, Math.min(170, width * 0.13));
  const onPage = chests.filter((c) => c.page === pos.page);
  const opened = chests.filter((c) => progress[c.topic.slug]?.game).length;
  const lastPage = pos.page === pageCount - 1;
  const allDone = chests.length > 0 && opened === chests.length;
  const end = pointAt(page.trail, m, 1);

  // Same spot mapPages.js checked for room (chestSlots), so what's drawn is
  // exactly what was spaced out.
  const chestSpot = (c) => chestPoint(page.trail, m, aspect, c.f, c.side);

  const region = regionAt(page, pos.f);
  const nextChest = chests.find((c) => c.page === pos.page + 1);
  const prevChest = [...chests].reverse().find((c) => c.page === pos.page - 1);

  return (
    <div className="tt">
      <div className="tt-bar">
        <div className="tt-bar-left">
          <h1>🗺️ {title || "Treasure Map"}</h1>
          <p>
            {pageCount > 1 ? `Map ${pos.page + 1} of ${pageCount} · ` : ""}
            {region ? `${region.emoji} ${mapName(pages, pos.page).replace(page.name, region.name)}` : mapName(pages, pos.page)} ·{" "}
            {teacher ? (
              <>
                your class opened <b>{classOpened}</b> of <b>{chests.length * classmates.length}</b> chests
              </>
            ) : (
              <>
                <b>{opened}</b> of <b>{chests.length}</b> chests opened
              </>
            )}
          </p>
        </div>
        <div className="tt-bar-right">
          {teacher && classmates.length > 0 && (
            <button type="button" className={`tt-btn is-soft ${showClass ? "is-on" : ""}`} onClick={() => setShowClass((v) => !v)}>
              👥 {showClass ? "Hide my class" : "Show my class"}
            </button>
          )}
          {prevChest && (
            <button type="button" className="tt-btn is-soft" disabled={walking} onClick={() => walkTo(prevChest)}>
              ◀ Back a map
            </button>
          )}
          {nextChest && (
            <button type="button" className="tt-btn" disabled={walking} onClick={() => walkTo(nextChest)}>
              Next map ▶
            </button>
          )}
        </div>
      </div>

      {(goal || (teacher && onGoalClick)) && <GoalBar goal={goal} teacher={teacher} onEdit={onGoalClick} />}

      <div className={`tt-map ${turning ? "is-turning" : ""}`} ref={boxRef} style={{ aspectRatio: `${aspect}` }}>
        <img className="tt-img" src={page.imageUrl} alt="" draggable="false" />

        {onPage.map((c) => {
          const s = chestSpot(c);
          const done = Boolean(progress[c.topic.slug]?.game);
          const locked = c.index >= lockedFrom;
          const isTarget = card?.topic.slug === c.topic.slug;
          const passing = walking && Math.abs(pos.f - c.f) < 0.035;
          const reg = regionAt(page, c.f);
          return (
            <button
              key={c.topic.slug || c.index}
              type="button"
              className={`tt-chest ${done ? "is-open" : ""} ${locked ? "is-locked" : ""} ${isTarget ? "is-here" : ""} ${passing ? "is-passing" : ""} ${opening.has(c.topic.slug) ? "is-opening" : ""}`}
              style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${Math.max(44, Math.min(96, width * 0.065))}px` }}
              onClick={() => (locked ? onLocked?.(c.topic) : walkTo(c))}
              disabled={walking}
              title={`${c.topic.title || "Topic"}${done ? " — opened!" : ""}`}
            >
              <ChestArt tier={done ? "gold" : "wood"} open={done} />
              <span className="tt-chest-label">
                <span className="tt-chest-num">{c.index + 1}</span> {c.topic.title || `Topic ${c.index + 1}`}
              </span>
              {locked && <span className="tt-chest-lock">🔒</span>}
              {teacher && classmates.length > 0 && (
                <span className={`tt-chest-count ${openedBy(c.topic.slug).length === classmates.length ? "is-all" : ""}`} title="Students who opened this chest">
                  ✅ {openedBy(c.topic.slug).length}/{classmates.length}
                </span>
              )}
              {reg && <span className="tt-chest-region">{reg.emoji}</span>}
            </button>
          );
        })}

        {/* the end of the trail: the next part, or the big treasure */}
        {lastPage ? (
          <div className={`tt-x ${allDone ? "is-found" : ""}`} style={{ left: `${end.x}%`, top: `${end.y}%` }} title={allDone ? "All the treasure found!" : "Open every chest to find the treasure!"}>
            {allDone ? "🏆" : "❌"}
          </div>
        ) : (
          <div className="tt-exit" style={{ left: `${end.x}%`, top: `${end.y}%` }}>
            ➜
          </div>
        )}

        {/* the class (teacher view) */}
        {teacher &&
          showClass &&
          onPage.map((c) => {
            const kids = kidsAt[c.topic.slug] || [];
            if (!kids.length) return null;
            const p = pointAt(page.trail, m, c.f);
            const len = Math.hypot(p.dx * aspect, p.dy) || 1;
            const side = -c.side; // the other side of the trail from the chest
            const nx = (-p.dy / len) * side;
            const ny = ((p.dx * aspect) / len) * side;
            const bx = p.x + (nx * 7) / aspect;
            const by = p.y + ny * 7 + 4;
            const shown = kids.slice(0, 4);
            const kidH = Math.max(44, avatarH * 0.5);
            const gap = ((kidH * 0.62) / Math.max(1, width)) * 100; // % between them
            return (
              <div
                key={`kids-${c.topic.slug}`}
                className="tt-kids"
                style={{ left: `${Math.max(5, Math.min(95, bx))}%`, top: `${Math.max(12, Math.min(96, by))}%` }}
              >
                {shown.map((k, i) => {
                  const lk = kidLooks[k.uid] || {};
                  return (
                    <button
                      key={k.uid}
                      type="button"
                      className="tt-kid"
                      style={{ left: `${(i - (shown.length - 1) / 2) * gap * (width / 100)}px`, height: kidH, zIndex: 10 - i }}
                      onClick={() => (onKidClick ? onKidClick(k.uid) : setCard(c))}
                      title={`${k.name} — tap to see how they're doing`}
                    >
                      <AvatarWithOutfit look={lk.look || null} outfit={lk.outfit || {}} size={kidH} full noBg />
                      <span className="tt-kid-name">{String(k.name || "").split(" ")[0]}</span>
                    </button>
                  );
                })}
                {kids.length > shown.length && (
                  <button type="button" className="tt-kids-more" onClick={() => setCard(c)} title={kids.map((k) => k.name).join(", ")}>
                    +{kids.length - shown.length}
                  </button>
                )}
              </div>
            );
          })}

        {/* you */}
        <div className={`tt-me ${walking ? "is-walking" : ""}`} style={{ left: `${here.x}%`, top: `${here.y}%`, height: avatarH }}>
          <div className="tt-me-flip" style={{ transform: `scaleX(${facing < 0 ? -1 : 1})` }}>
            <AvatarWithOutfit look={me.look} outfit={closet.outfit} size={avatarH} full noBg />
          </div>
          <span className="tt-me-shadow" />
        </div>

        {!walking && !card && (
          <div className="tt-hint">
            {teacher ? "👆 Tap a chest to see who opened it — tap a student to see how they're doing" : "👆 Tap a treasure chest — you'll walk there along the trail!"}
          </div>
        )}
        {allDone && lastPage && !walking && <div className="tt-hint is-gold">🏆 You found ALL the treasure in this class!</div>}
      </div>

      {card && (
        <div className="tt-card-pop" role="dialog" aria-modal="true" aria-label={card.topic.title} onMouseDown={(e) => e.target === e.currentTarget && setCard(null)}>
          <div className={`tt-card ${progress[card.topic.slug]?.game ? "is-open" : ""}`}>
            <div className="tt-card-chest">
              <ChestArt tier={progress[card.topic.slug]?.game ? "gold" : "wood"} open={Boolean(progress[card.topic.slug]?.game)} />
            </div>
            <p className="tt-card-kicker">
              Chest {card.index + 1}
              {regionAt(pageFor(pages, card.page), card.f) ? ` · ${regionAt(pageFor(pages, card.page), card.f).emoji} ${regionAt(pageFor(pages, card.page), card.f).name}` : ""}
            </p>
            <h2>{card.topic.title || "Topic"}</h2>
            <p className="tt-card-text">
              {teacher
                ? "Each student opens this chest by finishing the topic's game. Play it on your board with the class any time."
                : progress[card.topic.slug]?.game
                  ? "You already opened this chest! Play again any time for more coins and a better score."
                  : "Play this topic's game to open the chest!"}
            </p>
            {teacher && (
              <div className="tt-card-class">
                <p className="tt-card-kicker">
                  ✅ Opened it ({openedBy(card.topic.slug).length} of {classmates.length})
                </p>
                <div className="tt-card-kids">
                  {openedBy(card.topic.slug).length === 0 && <span className="tt-card-none">Nobody yet</span>}
                  {openedBy(card.topic.slug).map((k) => (
                    <button key={k.uid} type="button" className="tt-card-kid is-done" onClick={() => onKidClick?.(k.uid)}>
                      <span className="tt-card-kid-chest">
                        <ChestArt tier="gold" open />
                      </span>
                      {k.name}
                    </button>
                  ))}
                </div>
                {classmates.length > openedBy(card.topic.slug).length && (
                  <>
                    <p className="tt-card-kicker is-grey">⏳ Not yet</p>
                    <div className="tt-card-kids">
                      {classmates
                        .filter((k) => !k.done?.[card.topic.slug])
                        .map((k) => (
                          <button key={k.uid} type="button" className="tt-card-kid" onClick={() => onKidClick?.(k.uid)}>
                            {k.name}
                          </button>
                        ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="tt-card-actions">
              {card.topic.hasGame === false ? (
                <p className="tt-card-text">This topic doesn't have a game yet.</p>
              ) : (
                <button type="button" className="tt-btn is-big" onClick={() => onPlay?.(card.topic.slug)}>
                  ▶ {progress[card.topic.slug]?.game ? "Play again" : "Play the game"}
                </button>
              )}
              <button type="button" className="tt-btn is-soft" onClick={() => setCard(null)}>
                Look around
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasureTrail;
